/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  IKycDocumentState,
  KYC_DOCUMENTS,
  KycPresence,
  probeDocument,
} from 'utils/kycStatus';

/**
 * Real KYC state for a user. Each document URL is probed, because the
 * platform returns a URL for every slot whether or not a file exists.
 *
 * - A re-probe (the platform bumps `-0` to `-1` in the URL after an upload)
 *   keeps the last known state instead of flashing every row to "checking".
 * - `markUploaded` trusts a successful UploadKYCDocs for the rest of the
 *   visit, so storage lag can never flip a just-uploaded document back to
 *   missing.
 */
export default (user: Record<string, any> | undefined) => {
  const [presence, setPresence] = useState<
    Record<string, KycPresence>
  >({});
  const uploadedRef = useRef<Set<string>>(new Set());

  const urlsKey = KYC_DOCUMENTS.map(
    (doc) => user?.[doc.field] || '',
  ).join('|');

  const probe = useCallback(() => {
    let cancelled = false;
    setPresence((current) => {
      const next = { ...current };
      KYC_DOCUMENTS.forEach((doc) => {
        next[doc.key] = current[doc.key] ?? 'checking';
      });
      return next;
    });

    KYC_DOCUMENTS.forEach((doc) => {
      probeDocument(user?.[doc.field]).then((exists) => {
        if (cancelled) return;
        if (!exists && uploadedRef.current.has(doc.key)) return;
        setPresence((current) => ({
          ...current,
          [doc.key]: exists ? 'present' : 'missing',
        }));
      });
    });

    return () => {
      cancelled = true;
    };
  }, [urlsKey]);

  useEffect(() => {
    if (!user) return undefined;
    return probe();
  }, [probe, Boolean(user)]);

  const markUploaded = useCallback((key: string) => {
    uploadedRef.current.add(key);
    setPresence((current) => ({ ...current, [key]: 'present' }));
  }, []);

  const documents: IKycDocumentState[] = useMemo(
    () =>
      KYC_DOCUMENTS.map((doc) => ({
        ...doc,
        url: user?.[doc.field] || undefined,
        presence: presence[doc.key] || 'checking',
      })),
    [presence, urlsKey],
  );

  const required = documents.filter((doc) => doc.required);
  const isChecking = documents.some(
    (doc) => doc.presence === 'checking',
  );
  const done = required.filter(
    (doc) => doc.presence === 'present',
  ).length;

  return {
    documents,
    isChecking,
    done,
    total: required.length,
    isComplete: !isChecking && done === required.length,
    markUploaded,
  };
};
